import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { register, validateInvitation } from '../api';
import Captcha, { CAPTCHA_ENABLED, CAPTCHA_PROMPT } from '../components/Captcha';
import { AFTER_AUTH_REDIRECT } from '../config';
import '../styles/page-login.css';
import Seo from '../components/Seo';

/* The invite email links to /invite?token=…&email=…&org=…
   `org` + `token` are checked against /organizations/validate-invitation, and
   the account is then created through the normal /auth/register endpoint with
   isInvitationSignup set — which is what makes the user a member of the
   inviting workspace instead of creating a new one. */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function initials(text) {
  const w = (text || '').trim().split(/\s+/).filter(Boolean);
  if (!w.length) return 'TG';
  return (w[0][0] + (w[1]?.[0] || '')).toUpperCase();
}

export default function InvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const org = params.get('org') || '';
  const invitedEmail = params.get('email') || '';

  /* checking | ok | invalid | missing */
  const [state, setState] = useState(token && org ? 'checking' : 'missing');
  const [invite, setInvite] = useState(null);
  const [inviteErr, setInviteErr] = useState('');

  const [v, setV] = useState({ name: '', email: invitedEmail, password: '', confirm: '' });
  const [err, setErr] = useState({});
  const [busy, setBusy] = useState(false);
  const [joined, setJoined] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const captchaRef = useRef(null);

  useEffect(() => {
    if (!token || !org) return undefined;
    let cancelled = false;
    validateInvitation({ org, token })
      .then((r) => {
        if (cancelled) return;
        setInvite(r?.data || null);
        setState('ok');
      })
      .catch((e) => {
        if (cancelled) return;
        setInviteErr(
          e?.status === 400 || e?.status === 404
            ? 'This invitation has expired or has already been used.'
            : e?.message || 'We couldn’t check this invitation right now.',
        );
        setState('invalid');
      });
    return () => { cancelled = true; };
  }, [token, org]);

  const set = (name) => (e) => {
    const val = e.target.value;
    setV((s) => ({ ...s, [name]: val }));
    if (err[name]) setErr((s) => ({ ...s, [name]: '' }));
  };

  const check = () => {
    const next = {};
    const t = v.name.trim();
    const words = t.split(/\s+/).filter(Boolean);
    /* Same 2–50 rule the API enforces on firstName and lastName separately. */
    if (!t) next.name = 'Enter your name.';
    else if (words.length < 2) next.name = 'Enter your first and last name.';
    else if (words[0].length < 2) next.name = 'Your first name needs at least 2 letters.';
    else if (words.slice(1).join(' ').length < 2) next.name = 'Your last name needs at least 2 letters.';

    if (!v.email.trim()) next.email = 'Enter your email.';
    else if (!EMAIL_RE.test(v.email.trim())) next.email = 'That email address looks incomplete.';

    if (!v.password) next.password = 'Choose a password.';
    else if (v.password.length < 8) next.password = 'Use at least 8 characters.';

    if (v.confirm !== v.password) next.confirm = 'Passwords do not match.';
    return next;
  };

  const submit = (e) => {
    e.preventDefault();
    if (busy) return;
    const next = check();
    setErr(next);
    const bad = Object.keys(next)[0];
    if (bad) {
      document.getElementById(`iv-${bad}`)?.focus();
      return;
    }
    if (CAPTCHA_ENABLED && !captcha) {
      setErr({ form: CAPTCHA_PROMPT });
      return;
    }
    setBusy(true);
    setErr({});
    register({
      name: v.name,
      email: v.email,
      password: v.password,
      recaptchaToken: captcha,
      invitation: { token: invite?.token || token, organizationId: invite?.organizationId || org },
    })
      .then(() => {
        setJoined(true);
        /* Invitation signups come back activated with a token already stored,
           so go straight into the app the way the host's Register.js does. */
        window.location.assign(AFTER_AUTH_REDIRECT);
      })
      .catch((e2) => {
        setErr({
          form:
            e2?.status === 409
              ? 'An account with this email already exists — sign in instead.'
              : e2?.message || 'Something went wrong. Please try again.',
        });
      })
      .finally(() => {
        setBusy(false);
        try { captchaRef.current?.reset(); } catch { /* not mounted */ }
        setCaptcha(null);
      });
  };

  const orgName = invite?.organizationName || '';

  return (
    <>
      <Seo title="Accept Your Invitation | TeamGrid™" description="Accept your invitation and join your team on TeamGrid. Set a password to activate the account. No screenshots, no keystrokes, just work patterns." noindex />
      <div className="auth-wrap">
        <div className="auth-card">
          <img className="amark" src="/assets/mark-teamgrid.svg" alt="TeamGrid" />

          {state === 'checking' && (
            <>
              <h1>Checking your invitation…</h1>
              <p className="asub">One moment while we confirm the link is still valid.</p>
            </>
          )}

          {(state === 'invalid' || state === 'missing') && (
            <>
              <h1>We couldn’t open this invite</h1>
              <p className="asub">
                {state === 'missing'
                  ? 'This link is missing the details we need to identify the workspace.'
                  : inviteErr}
              </p>
              <p style={{ fontSize: '13px', lineHeight: '21px', color: 'var(--ink-soft)', marginTop: '14px' }}>
                Ask whoever invited you to send a fresh link, or{' '}
                <a href="/register" style={{ color: 'var(--teal-deep)', fontWeight: 700 }}>create your own workspace</a>.
              </p>
              <a className="btn-primary" href="/register" style={{ width: '100%', marginTop: '20px' }}>Start a free trial</a>
              <p className="aswitch">Already have an account? <a href="/login">Sign in</a></p>
            </>
          )}

          {state === 'ok' && !joined && (
            <>
              <h1>You&apos;re invited</h1>
              <p className="asub">Set a password to activate your account and join the workspace.</p>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#f6fafa', border: '1px solid var(--line)', borderRadius: '12px', padding: '14px 16px', margin: '0 0 4px' }}>
                <span style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#e6f5f3', color: '#007A6E', fontSize: '14px', fontWeight: '800', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{initials(orgName)}</span>
                <span>
                  <div style={{ fontSize: '13.5px', fontWeight: '700', color: 'var(--ink-strong)' }}>Join {orgName}</div>
                  <div style={{ fontSize: '12.5px', color: 'var(--ink-soft)' }}>You&apos;ll be added as a team member</div>
                </span>
              </div>

              <form onSubmit={submit} noValidate>
                <label htmlFor="iv-name">Your full name</label>
                <input id="iv-name" autoComplete="name" placeholder="Priya Nair" value={v.name} onChange={set('name')} aria-invalid={!!err.name} />
                {err.name && <p style={{ fontSize: '12px', color: '#E0483D', fontWeight: 600, marginTop: '6px' }}>{err.name}</p>}

                <label htmlFor="iv-email">Your email</label>
                <input
                  id="iv-email"
                  type="email"
                  autoComplete="username"
                  value={v.email}
                  onChange={set('email')}
                  readOnly={!!invitedEmail}
                  style={invitedEmail ? { opacity: 0.7, cursor: 'not-allowed' } : undefined}
                  aria-invalid={!!err.email}
                />
                {err.email && <p style={{ fontSize: '12px', color: '#E0483D', fontWeight: 600, marginTop: '6px' }}>{err.email}</p>}

                <label htmlFor="iv-password">Password</label>
                <input id="iv-password" type="password" autoComplete="new-password" placeholder="At least 8 characters" value={v.password} onChange={set('password')} aria-invalid={!!err.password} />
                {err.password && <p style={{ fontSize: '12px', color: '#E0483D', fontWeight: 600, marginTop: '6px' }}>{err.password}</p>}

                <label htmlFor="iv-confirm">Confirm password</label>
                <input id="iv-confirm" type="password" autoComplete="new-password" placeholder="Repeat your password" value={v.confirm} onChange={set('confirm')} aria-invalid={!!err.confirm} />
                {err.confirm && <p style={{ fontSize: '12px', color: '#E0483D', fontWeight: 600, marginTop: '6px' }}>{err.confirm}</p>}

                <Captcha ref={captchaRef} onChange={setCaptcha} />

                {err.form && <p style={{ fontSize: '12.5px', color: '#E0483D', fontWeight: 600, marginTop: '12px' }} role="alert">{err.form}</p>}

                <button className="btn-primary" type="submit" disabled={busy} style={{ width: '100%', marginTop: '18px' }}>
                  {busy ? 'Activating…' : 'Accept invitation'}
                </button>
              </form>

              <div className="trialnote">
                <span>NO SCREENSHOTS</span><span>NO KEYSTROKES</span><span>PATTERNS, NOT PEOPLE</span>
              </div>

              <p style={{ fontSize: '11.5px', lineHeight: '18px', color: 'var(--ink-faint)', textAlign: 'center', marginTop: '14px' }}>
                By accepting, you agree to our <a href="/terms" style={{ color: 'var(--teal-deep)', fontWeight: '700' }}>terms of service</a> and{' '}
                <a href="/privacy" style={{ color: 'var(--teal-deep)', fontWeight: '700' }}>privacy policy</a>.
              </p>

              <p className="aswitch">Not expecting this? <a href="/contact">Tell us</a> and we&apos;ll cancel the invite.</p>
            </>
          )}

          {joined && (
            <div className="oksplash show">
              <div className="okring">
                <svg aria-hidden="true" width="28" height="28" viewBox="0 0 16 16"><path fill="none" stroke="#1d7a43" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M2 8.5 6 12.5 14 3.5" /></svg>
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: '800', color: 'var(--ink-strong)' }}>Welcome to {orgName}</h3>
              <p style={{ fontSize: '13.5px', color: 'var(--ink-soft)', marginTop: '8px' }}>Taking you into your workspace…</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
